// Automatizaciones · editor de flujo: disparador, pasos (lista vertical) y configuración.
import type { JSX, ReactNode } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Save, Trash2 } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { Button, Card, ErrorState, Field, Input, Loading, Select, Textarea } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import {
  defaultConfig, defaultSettings, type Flow, type FlowSettings, type Lookups, newStepId, STEP_META, type Step, type StepConfig,
  type StepType, type Trigger, TRIGGER_LABELS, type TriggerType, useFlowLookups,
} from "./shared.tsx";

const VARS_HINT = "Variables: {{nombre}} {{apellido}} {{empresa}} {{email}}";

export function FlowEditor(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const lk = useFlowLookups();
  const existing = useQuery<{ flow: Flow }>({ queryKey: ["mkt-flow", id], queryFn: () => api.get(`/mkt/flows/${id}`), enabled: Boolean(id) });

  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState<Trigger>({ type: "form_submit", refId: "" });
  const [steps, setSteps] = useState<Step[]>([]);
  const [settings, setSettings] = useState<FlowSettings>(defaultSettings());
  const [loaded, setLoaded] = useState(!id);

  useEffect(() => {
    const f = existing.data?.flow;
    if (!f || loaded) return;
    // Flujos importados de Clientify no traen lanzador: se propone Formulario de Facebook.
    const known = Object.prototype.hasOwnProperty.call(TRIGGER_LABELS, f.trigger?.type ?? "");
    setName(f.name); setTrigger(known ? { type: f.trigger.type, refId: f.trigger.refId ?? "" } : { type: "fb_lead", refId: "" }); setSteps(f.steps); setSettings(f.settings); setLoaded(true);
  }, [existing.data, loaded]);

  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), trigger, steps, settings };
      if (id) { await api.put(`/mkt/flows/${id}`, body); return id; }
      const r = await api.post<{ id: string }>("/mkt/flows", body);
      return r.id;
    },
    onSuccess: (fid) => {
      toast.success("Flujo guardado.");
      void qc.invalidateQueries({ queryKey: ["mkt-flows"] });
      void qc.invalidateQueries({ queryKey: ["mkt-flow", fid] });
      nav(`/marketing/automatizaciones/${fid}`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
  });

  if (!me.data?.admin) return <Card className="p-6"><p className="text-sm text-ink-muted">Solo administradores pueden editar automatizaciones.</p></Card>;
  if (lk.isError || existing.isError) return <ErrorState message="No se pudo cargar el flujo." />;
  if (lk.isLoading || !lk.data || !loaded) return <Loading />;
  const L = lk.data;

  const insertAt = (i: number, type: StepType): void => {
    const s: Step = { id: newStepId(), type, config: defaultConfig(type, L) };
    setSteps((prev) => [...prev.slice(0, i), s, ...prev.slice(i)]);
  };
  const move = (i: number, d: -1 | 1): void => setSteps((prev) => {
    const j = i + d; if (j < 0 || j >= prev.length) return prev;
    const next = [...prev]; [next[i], next[j]] = [next[j]!, next[i]!]; return next;
  });
  const update = (i: number, cfg: StepConfig): void => setSteps((prev) => prev.map((s, k) => (k === i ? { ...s, config: cfg } : s)));
  const remove = (i: number): void => setSteps((prev) => prev.filter((_, k) => k !== i));

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button className="btn-ghost btn-sm" onClick={() => nav(id ? `/marketing/automatizaciones/${id}` : "/marketing/automatizaciones")}>
          <ArrowLeft size={16} strokeWidth={1.75} /> Volver
        </button>
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending || !name.trim()}>
          <Save size={16} strokeWidth={1.75} /> {save.isPending ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      <Card className="p-5 space-y-4">
        <Field label="Nombre del flujo" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={160} placeholder="Ej.: Leads Facebook Perú" />
        </Field>
        <TriggerEditor trigger={trigger} onChange={setTrigger} lk={L} />
      </Card>

      <div>
        <h3 className="text-sm font-semibold text-ink mb-2">Pasos</h3>
        <AddStep onAdd={(t) => insertAt(0, t)} />
        {steps.map((s, i) => (
          <div key={s.id}>
            <StepCard index={i} total={steps.length} step={s} steps={steps} lk={L}
              onChange={(c) => update(i, c)} onMove={(d) => move(i, d)} onRemove={() => remove(i)} />
            <AddStep onAdd={(t) => insertAt(i + 1, t)} />
          </div>
        ))}
        {steps.length === 0 && <p className="text-sm text-ink-muted px-1">Añade el primer paso del flujo.</p>}
      </div>

      <SettingsEditor settings={settings} onChange={setSettings} lk={L} />
    </div>
  );
}

// ────────── Disparador ──────────
function TriggerEditor({ trigger, onChange, lk }: { trigger: Trigger; onChange: (t: Trigger) => void; lk: Lookups }): JSX.Element {
  const options: Record<TriggerType, Array<{ id: string; name: string }> | null> = {
    form_submit: lk.forms, fb_lead: lk.fbForms, survey_response: lk.surveys, list_added: lk.lists, email_click: lk.campaigns, tag_added: null,
  };
  const opts = options[trigger.type];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Disparador (lanzador)">
        <Select value={trigger.type} onChange={(e) => onChange({ type: e.target.value as TriggerType, refId: "" })}>
          {(Object.keys(TRIGGER_LABELS) as TriggerType[]).map((t) => <option key={t} value={t}>{TRIGGER_LABELS[t]}</option>)}
        </Select>
      </Field>
      {opts ? (
        <Field label="¿Cuál?" hint={trigger.type === "fb_lead" && !opts.length ? "Sincroniza los formularios en Configuración → APIs → Facebook." : undefined}>
          <Select value={trigger.refId} onChange={(e) => onChange({ ...trigger, refId: e.target.value })}>
            <option value="">Cualquiera</option>
            {opts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </Select>
        </Field>
      ) : (
        <Field label="Etiqueta" hint="Vacío = cualquier etiqueta">
          <Input value={trigger.refId} maxLength={30} list="flow-tags" onChange={(e) => onChange({ ...trigger, refId: e.target.value })} />
          <datalist id="flow-tags">{lk.tags.map((t) => <option key={t} value={t} />)}</datalist>
        </Field>
      )}
    </div>
  );
}

// ────────── Añadir paso ──────────
function AddStep({ onAdd }: { onAdd: (t: StepType) => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col items-center py-1.5">
      <div className="w-px h-3 bg-line" />
      <button type="button" className="btn-ghost btn-sm rounded-full border border-dashed border-line" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <Plus size={14} strokeWidth={1.75} /> Añadir paso
      </button>
      {open && (
        <div className="mt-2 w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5 card p-2">
          {(Object.keys(STEP_META) as StepType[]).map((t) => {
            const m = STEP_META[t]; const Icon = m.icon;
            return (
              <button key={t} type="button" disabled={Boolean(m.disabled)} title={m.disabled}
                onClick={() => { onAdd(t); setOpen(false); }}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-left text-ink-secondary hover:bg-surface-hover hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed">
                <Icon size={15} strokeWidth={1.75} className="shrink-0" />
                <span>{m.label}{m.disabled && <span className="block text-[11px] text-ink-muted">{m.disabled}</span>}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className="w-px h-3 bg-line" />
    </div>
  );
}

// ────────── Paso ──────────
function StepCard({ index, total, step, steps, lk, onChange, onMove, onRemove }: {
  index: number; total: number; step: Step; steps: Step[]; lk: Lookups;
  onChange: (c: StepConfig) => void; onMove: (d: -1 | 1) => void; onRemove: () => void;
}): JSX.Element {
  const m = STEP_META[step.type]; const Icon = m.icon;
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="grid place-items-center w-6 h-6 rounded-full bg-primary-soft text-primary text-xs font-semibold">{index + 1}</span>
        <Icon size={16} strokeWidth={1.75} className="text-primary" />
        <span className="font-medium text-ink flex-1">{m.label}</span>
        <button className="btn-ghost btn-sm" title="Subir" disabled={index === 0} onClick={() => onMove(-1)}><ArrowUp size={14} strokeWidth={1.75} /></button>
        <button className="btn-ghost btn-sm" title="Bajar" disabled={index === total - 1} onClick={() => onMove(1)}><ArrowDown size={14} strokeWidth={1.75} /></button>
        <button className="btn-ghost btn-sm text-danger" title="Eliminar paso" onClick={onRemove}><Trash2 size={14} strokeWidth={1.75} /></button>
      </div>
      <StepFields step={step} index={index} steps={steps} lk={lk} onChange={onChange} />
    </Card>
  );
}

const Grid = ({ children }: { children: ReactNode }): JSX.Element => <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;

function StepFields({ step, index, steps, lk, onChange }: { step: Step; index: number; steps: Step[]; lk: Lookups; onChange: (c: StepConfig) => void }): JSX.Element {
  const c = step.config;
  const set = (k: string, v: string | number | boolean | undefined): void => onChange({ ...c, [k]: v });
  const str = (k: string): string => String(c[k] ?? "");
  const num = (k: string): string => (c[k] === undefined || c[k] === "" ? "" : String(c[k]));
  const toNum = (v: string): number | undefined => (v === "" ? undefined : Number(v));
  const userSelect = (k: string): JSX.Element => (
    <Select value={str(k)} onChange={(e) => set(k, e.target.value || undefined)}>
      <option value="">— Selecciona —</option>
      {lk.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
    </Select>
  );

  switch (step.type) {
    case "send_email":
      return (
        <div className="space-y-3">
          <Field label="Asunto" required><Input value={str("subject")} maxLength={250} onChange={(e) => set("subject", e.target.value)} /></Field>
          <Field label="Contenido (HTML simple)" hint={`${VARS_HINT}. Se eliminan scripts y estilos peligrosos.`}>
            <Textarea rows={6} value={str("html")} onChange={(e) => set("html", e.target.value)} className="font-mono text-xs" />
          </Field>
        </div>
      );
    case "wait":
      return (
        <Grid>
          <Field label="Tipo de espera">
            <Select value={str("mode")} onChange={(e) => onChange(e.target.value === "until" ? { mode: "until", until: "" } : { mode: "duration", amount: 1, unit: "days" })}>
              <option value="duration">Durante un tiempo</option>
              <option value="until">Hasta una fecha</option>
            </Select>
          </Field>
          {str("mode") === "until" ? (
            <Field label="Fecha y hora">
              <Input type="datetime-local" value={str("until") ? toLocalInput(str("until")) : ""}
                onChange={(e) => set("until", e.target.value ? new Date(e.target.value).toISOString() : "")} />
            </Field>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Cantidad"><Input type="number" min={1} max={10000} value={num("amount")} onChange={(e) => set("amount", toNum(e.target.value))} /></Field>
              <Field label="Unidad">
                <Select value={str("unit")} onChange={(e) => set("unit", e.target.value)}>
                  <option value="minutes">Minutos</option><option value="hours">Horas</option><option value="days">Días</option>
                </Select>
              </Field>
            </div>
          )}
        </Grid>
      );
    case "add_tag":
    case "remove_tag":
      return (
        <Field label="Etiqueta">
          <Input value={str("tag")} maxLength={30} list={`tags-${step.id}`} onChange={(e) => set("tag", e.target.value)} />
          <datalist id={`tags-${step.id}`}>{lk.tags.map((t) => <option key={t} value={t} />)}</datalist>
        </Field>
      );
    case "add_to_list":
      return (
        <Field label="Lista">
          <Select value={str("listId")} onChange={(e) => set("listId", e.target.value)}>
            <option value="">— Selecciona —</option>
            {lk.lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
        </Field>
      );
    case "create_deal": {
      const pipe = lk.pipelines.find((p) => p.id === str("pipelineId"));
      return (
        <div className="space-y-3">
          <Grid>
            <Field label="Pipeline">
              <Select value={str("pipelineId")} onChange={(e) => {
                const p = lk.pipelines.find((x) => x.id === e.target.value);
                onChange({ ...c, pipelineId: e.target.value, stageId: p?.stages.find((s) => s.type === "open")?.id ?? "" });
              }}>
                <option value="">— Selecciona —</option>
                {lk.pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
            <Field label="Etapa">
              <Select value={str("stageId")} onChange={(e) => set("stageId", e.target.value)}>
                <option value="">— Selecciona —</option>
                {(pipe?.stages ?? []).filter((s) => s.type !== "closed_lost").map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </Field>
          </Grid>
          <Field label="Nombre de la oportunidad" hint={VARS_HINT}><Input value={str("name")} maxLength={120} onChange={(e) => set("name", e.target.value)} /></Field>
          <Grid>
            <Field label="Importe"><Input type="number" min={0} value={num("amount")} onChange={(e) => set("amount", toNum(e.target.value) ?? 0)} /></Field>
            <Field label="Fecha de cierre (días desde hoy)"><Input type="number" min={0} max={3650} value={num("closingDays")} onChange={(e) => set("closingDays", toNum(e.target.value) ?? 0)} /></Field>
          </Grid>
          <p className="text-xs text-ink-muted">Se usa la cuenta del contacto; si no tiene, se crea “Nombre Apellido (particular)”.</p>
        </div>
      );
    }
    case "create_task":
      return (
        <div className="space-y-3">
          <Field label="Asunto" hint={VARS_HINT}><Input value={str("subject")} maxLength={200} onChange={(e) => set("subject", e.target.value)} /></Field>
          <Grid>
            <Field label="Vence en (días)"><Input type="number" min={0} max={365} value={num("dueDays")} onChange={(e) => set("dueDays", toNum(e.target.value) ?? 0)} /></Field>
            <Field label="Prioridad">
              <Select value={str("priority")} onChange={(e) => set("priority", e.target.value)}>
                <option>Alta</option><option>Media</option><option>Baja</option>
              </Select>
            </Field>
            <Field label="Asignar a">
              <Select value={str("assignTo")} onChange={(e) => set("assignTo", e.target.value)}>
                <option value="contact_owner">Propietario del contacto</option>
                <option value="flow_owner">Propietario del flujo</option>
                <option value="user">Un usuario</option>
              </Select>
            </Field>
            {str("assignTo") === "user" && <Field label="Usuario">{userSelect("userId")}</Field>}
          </Grid>
        </div>
      );
    case "notify":
      return (
        <div className="space-y-3">
          <Grid>
            <Field label="Destinatario (in-app)">
              <Select value={str("target")} onChange={(e) => set("target", e.target.value)}>
                <option value="contact_owner">Propietario del contacto</option>
                <option value="flow_owner">Propietario del flujo</option>
                <option value="user">Un usuario</option>
              </Select>
            </Field>
            {str("target") === "user" && <Field label="Usuario">{userSelect("userId")}</Field>}
          </Grid>
          <label className="flex items-center gap-2 text-sm text-ink-secondary">
            <input type="checkbox" checked={Boolean(c["inApp"])} onChange={(e) => set("inApp", e.target.checked)} /> Notificación in-app
          </label>
          <Field label="Email interno (opcional)"><Input type="email" value={str("email")} maxLength={200} onChange={(e) => set("email", e.target.value)} placeholder="ventas@empresa.com" /></Field>
          <Field label="Mensaje" hint={VARS_HINT}><Input value={str("message")} maxLength={500} onChange={(e) => set("message", e.target.value)} /></Field>
        </div>
      );
    case "add_score":
      return <Field label="Puntos (negativo para restar)"><Input type="number" min={-1000} max={1000} value={num("points")} onChange={(e) => set("points", toNum(e.target.value) ?? 0)} /></Field>;
    case "set_status":
      return (
        <Field label="Nuevo estado de marketing">
          <Input value={str("status")} maxLength={40} list={`st-${step.id}`} onChange={(e) => set("status", e.target.value)} />
          <datalist id={`st-${step.id}`}>{lk.statuses.map((s) => <option key={s} value={s} />)}</datalist>
        </Field>
      );
    case "set_owner":
      return <Field label="Nuevo propietario">{userSelect("userId")}</Field>;
    case "condition": {
      const later = steps.map((s, i) => ({ s, n: i + 1 })).filter((x) => x.n > index + 1);
      return (
        <div className="space-y-3">
          <Grid>
            <Field label="Si el contacto…">
              <Select value={str("check")} onChange={(e) => set("check", e.target.value)}>
                <option value="has_tag">tiene la etiqueta</option>
                <option value="status_eq">tiene el estado</option>
                <option value="score_gte">tiene puntaje mayor o igual a</option>
                <option value="custom_eq">tiene el campo personalizado igual a</option>
              </Select>
            </Field>
            {str("check") === "custom_eq" && (
              <Field label="Campo">
                <Select value={str("key")} onChange={(e) => set("key", e.target.value)}>
                  <option value="">— Selecciona —</option>
                  {lk.customFields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </Select>
              </Field>
            )}
            <Field label="Valor"><Input value={str("value")} maxLength={120} type={str("check") === "score_gte" ? "number" : "text"} onChange={(e) => set("value", e.target.value)} /></Field>
          </Grid>
          <Grid>
            <Field label="Si se cumple">
              <Input value="Continuar con el siguiente paso" disabled />
            </Field>
            <Field label="Si no se cumple">
              <Select value={str("onFalse") === "goto" ? String(c["gotoStep"] ?? "") : "end"}
                onChange={(e) => (e.target.value === "end" ? onChange({ ...c, onFalse: "end", gotoStep: undefined }) : onChange({ ...c, onFalse: "goto", gotoStep: Number(e.target.value) }))}>
                <option value="end">Terminar el flujo</option>
                {later.map((x) => <option key={x.s.id} value={x.n}>Saltar al paso {x.n} ({STEP_META[x.s.type].label})</option>)}
              </Select>
            </Field>
          </Grid>
        </div>
      );
    }
    case "send_whatsapp":
      return <p className="text-sm text-ink-muted">Requiere WhatsApp Business API: este paso se omite al ejecutar el flujo.</p>;
  }
}

function toLocalInput(iso: string): string {
  const d = new Date(iso); if (Number.isNaN(d.getTime())) return "";
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ────────── Configuración ──────────
function SettingsEditor({ settings, onChange, lk }: { settings: FlowSettings; onChange: (s: FlowSettings) => void; lk: Lookups }): JSX.Element {
  const set = <K extends keyof FlowSettings>(k: K, v: FlowSettings[K]): void => onChange({ ...settings, [k]: v });
  const check = (k: "oncePerContact" | "singleFlow" | "stopOnGoal" | "stopOnMeeting" | "multipleDeals", label: string, hint?: string): JSX.Element => (
    <label key={k} className="flex items-start gap-2 text-sm text-ink-secondary">
      <input type="checkbox" className="mt-0.5" checked={settings[k]} onChange={(e) => set(k, e.target.checked)} />
      <span>{label}{hint && <span className="block text-xs text-ink-muted">{hint}</span>}</span>
    </label>
  );
  return (
    <Card className="p-5 space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-ink mb-2">Quién puede entrar</h3>
        <div className="space-y-2">
          {check("oncePerContact", "El contacto pasa por este flujo solo una vez")}
          {check("singleFlow", "No puede estar en más de un flujo a la vez")}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-ink mb-2">Objetivo</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="El objetivo se cumple cuando">
            <Select value={settings.goal.type} onChange={(e) => set("goal", { type: e.target.value as FlowSettings["goal"]["type"], value: "" })}>
              <option value="none">Sin objetivo</option>
              <option value="deal_won">Tiene una oportunidad ganada</option>
              <option value="status">Tiene el estado…</option>
              <option value="tag">Tiene la etiqueta…</option>
            </Select>
          </Field>
          {(settings.goal.type === "status" || settings.goal.type === "tag") && (
            <Field label={settings.goal.type === "status" ? "Estado" : "Etiqueta"}>
              <Input value={settings.goal.value} maxLength={60} list="goal-opts" onChange={(e) => set("goal", { ...settings.goal, value: e.target.value })} />
              <datalist id="goal-opts">{(settings.goal.type === "status" ? lk.statuses : lk.tags).map((s) => <option key={s} value={s} />)}</datalist>
            </Field>
          )}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-ink mb-2">Detener el flujo para el contacto que</h3>
        <div className="space-y-2">
          {check("stopOnGoal", "Alcanza el objetivo")}
          <label className="flex items-start gap-2 text-sm text-ink-muted">
            <input type="checkbox" className="mt-0.5" disabled checked={false} readOnly />
            <span>Responde un email del flujo<span className="block text-xs">No disponible: requiere bandeja de entrada conectada.</span></span>
          </label>
          {check("stopOnMeeting", "Agenda una reunión", "Se detiene si se crea una reunión con el contacto después de entrar al flujo.")}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-ink mb-2">Avanzado</h3>
        {check("multipleDeals", "Permitir múltiples oportunidades por contacto", "Si está desactivado, no se crea una oportunidad si el contacto ya tiene una abierta.")}
      </div>
    </Card>
  );
}
